import { ApplicationRef, ChangeDetectorRef, ComponentRef, createComponent, EventEmitter, Injector, NgZone, OnChanges, reflectComponentType, SimpleChange, SimpleChanges } from '@angular/core'
import { WithProperties } from '@angular/elements'
import { Observable, ReplaySubject, Subscription, merge } from 'rxjs'
import { map, switchMap } from 'rxjs/operators'
import { getDefaultAttributeToPropertyInputs } from './get-default-attribute-to-property-inputs'
import { Scheduler } from './scheduler'

/**
 * Type of the constructor for the custom element created from an Angular component
 *
 * @internal
 */
export type CustomElementConstructor<T, E extends HTMLElement = HTMLElement> = new (...args) => E & WithProperties<T>

const CREATE_CUSTOM_ELEMENT_DESTROY_DELAY = 10

/**
 * Additional options to configure how the custom element is created from an Angular component
 */
export interface CreateCustomElementOptions<T extends HTMLElement = HTMLElement> {

  /**
   * The injector context used for the Angular component
   */
  injector: Injector

  /**
   * The constructor of the HTMLElement that will be used as the base for the custom element
   */
  base?: new (...args) => T

  /**
   * A collection of style sheets that will be adopted by the shadow DOM of the custom element
   */
  styleSheets?: Array<CSSStyleSheet>
}

/**
 * Create a framework agnostic custom element that can be used anywhere.
 *
 * The custom element is created through adapting a standalone Angular Component.
 *
 * @param component The class of the Angular Component that is being converted
 * @param configs   The configurations for the adaptation
 *
 * @returns         A class that extends HTMLElement
 */
export function createCustomElement<T, E extends HTMLElement = HTMLElement>(
  component: new (...args) => T,
  configs: CreateCustomElementOptions<E>
): CustomElementConstructor<T, E> {
  let componentMirror = reflectComponentType(component)

  if (!componentMirror) {
    throw new Error(`Unable to create a custom element for the ${ component } Angular component.`)
  }

  let injector = configs.injector

  let adoptedStyleSheets = configs.styleSheets ?? []

  // Get the attributes that map to the inputs of the component
  let attributeToPropertyInputs = getDefaultAttributeToPropertyInputs(componentMirror.inputs)

  // Get the base class for the element
  let BaseElement: new (...args) => HTMLElement = configs?.base ?? HTMLElement

  /**
   * Create a class that extends the base element
   *
   * The logic of this class is co-opted from Angular Elements, as they don't expose this API,
   * and we explicitly need to change how it works.
   */
  class ExtendedElement extends BaseElement {

    /**
     * Proxy the properties of the custom element to the properties and methods
     * of the Angular Component decorated with {@link Input}
     */
    static {
      componentMirror?.inputs?.forEach(({ propName }) => {
        Object.defineProperty(this.prototype, propName, {
          get(): unknown {
            return this.getInputValue(propName)
          },
          set(newValue: unknown): void {
            this.setInputValue(propName, newValue)
          },
          configurable: true,
          enumerable: true,
        })
      })
    }

    /**
     * The observed attributes of the element.
     *
     * @see {@link https://github.com/angular/angular/blob/15.2.x/packages/elements/src/create-custom-element.ts#L140 Comment } for the reason as to why the this is using quoted access.
     */
    static readonly ['observedAttributes'] = Object.keys(attributeToPropertyInputs)

    /**
     * A subscription to change, connect, and disconnect events in the custom element.
     */
    protected ngElementEventsSubscription: Subscription | null = null

    /**
     * Initial input values that were set before the component was created.
     */
    private readonly initialInputValues = new Map<string, unknown>()

    /**
     * Whether the created component implements the `OnChanges` interface.
     */
    private implementsOnChanges = false

    /**
     * Subject of `NgElementStrategyEvent` observables corresponding to the component's outputs.
     */
    private eventEmitters = new ReplaySubject<Observable<{ name: string; value: unknown }>[]>(1)

    /**
     * Merged stream of the component's output events.
     */
    readonly events = this.eventEmitters.pipe(switchMap(emitters => merge(...emitters)))

    /**
     * Reference to the component that was created on connect.
     */
    private componentRef: ComponentRef<T> | null = null

    /**
     * Reference to the component view's `ChangeDetectorRef`.
     */
    private viewChangeDetectorRef: ChangeDetectorRef | null = null

    /**
     * Changes that have been made to component inputs since the last change detection run.
     *
     * @remarks
     *
     * These are only recorded if the component implements the `OnChanges` interface.
     */
    private inputChanges: SimpleChanges | null = null

    /**
     * Whether changes have been made to component inputs since the last change detection run.
     */
    private hasInputChanges = false

    /**
     * Whether a change detection has been scheduled to run on the component.
     */
    private scheduledChangeDetectionFn: (() => void) | null = null

    /**
     * Callback function that when called will cancel a scheduled destruction on the component.
     */
    private scheduledDestroyFn: (() => void) | null = null

    /**
     * Set of component inputs that have not yet changed, i.e. for which `recordInputChange()` has not
     * fired.
     *
     * This helps detect the first change of an input, even if it is explicitly set to `undefined`.
     */
    private readonly unchangedInputs = new Set<string>(componentMirror?.inputs?.map(({ propName }) => propName) ?? [])

    /**
     * Service for setting zone context.
     */
    private readonly ngZone = injector.get<NgZone>(NgZone)

    /**
     * The zone the element was created in or `null` if Zone.js is not loaded.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private readonly elementZone = typeof (globalThis as any).Zone === 'undefined' ?
      null :
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      this.ngZone.run(() => (globalThis as any).Zone.current)

    constructor(...args) {
      super(...args)

      // Re-apply pre-existing input values (set as properties on the element)
      componentMirror?.inputs?.forEach(({ propName }) => {
        if (!Object.hasOwn(this, propName)) {
          // No pre-existing value for `propName`.
          return
        }

        // Delete the property from the instance and re-apply
        const value = this[propName]
        delete this[propName]
        this.setInputValue(propName, value)
      })
    }

    /**
     *
     * @param attrName
     * @param _oldValue
     * @param newValue
     * @param _namespace
     */
    attributeChangedCallback(attrName: string, _oldValue: string | null, newValue: string, _namespace?: string): void {
      let propName = attributeToPropertyInputs?.[attrName]
      if (propName) {
        this.setInputValue(propName, newValue)
      }
    }

    /**
     *
     */
    connectedCallback(): void {
      let subscribedToEvents = false

      // `events` are already available: Subscribe to it asap.
      if (this.events) {
        this.subscribeToEvents()
        subscribedToEvents = true
      }

      /**
       * Initializes a new component if one has not yet been created and cancels any scheduled
       * destruction.
       */
      this.runInZone(() => {
        // If the element is marked to be destroyed, cancel the task since the component was reconnected
        if (this.scheduledDestroyFn !== null) {
          this.scheduledDestroyFn()
          this.scheduledDestroyFn = null
          return
        }

        if (this.componentRef === null) {
          this.initializeComponent()
        }
      })

      if (!subscribedToEvents) {
        // `events` were not initialized before running `connect()`: Subscribe to them now.
        // The events emitted during the component initialization have been missed, but at least
        // future events will be captured.
        this.subscribeToEvents()
      }
    }

    /**
     * Schedules the component to be destroyed after some small delay in case the element is just
     * being moved across the DOM.
     */
    disconnectedCallback(): void {
      this.runInZone(() => {
        // Return if there is no componentRef or the component is already scheduled for destruction
        if (this.componentRef === null || this.scheduledDestroyFn !== null) {
          return
        }

        // Schedule the component to be destroyed after a small timeout in case it is being
        // moved elsewhere in the DOM
        this.scheduledDestroyFn = Scheduler.schedule(() => {
          if (this.componentRef !== null) {
            this.componentRef.destroy()
            this.componentRef = null
            this.viewChangeDetectorRef = null
          }
        }, CREATE_CUSTOM_ELEMENT_DESTROY_DELAY)
      })

      if (this.ngElementEventsSubscription) {
        this.ngElementEventsSubscription.unsubscribe()
        this.ngElementEventsSubscription = null
      }
    }

    /**
     * Creates a new component through the component factory with the provided element host and
     * sets up its initial inputs, listens for outputs changes, and runs an initial change detection.
     */
    protected initializeComponent() {
      let applicationRef = injector.get<ApplicationRef>(ApplicationRef)

      let environmentInjector = applicationRef.injector
      let elementInjector = Injector.create({ providers: [], parent: injector })

      let projectableNodes = []

      this.componentRef = createComponent<T>(component, {
        environmentInjector: environmentInjector,
        hostElement: this as unknown as HTMLElement,
        elementInjector: elementInjector,
        projectableNodes: projectableNodes
      })

      this.viewChangeDetectorRef = this.componentRef.injector.get<ChangeDetectorRef>(ChangeDetectorRef)

      this.implementsOnChanges = typeof (this.componentRef.instance as OnChanges).ngOnChanges === 'function'

      this.initializeInputs()
      this.initializeOutputs(this.componentRef)

      this.detectChanges()

      applicationRef.attachView(this.componentRef.hostView)

      this.adoptStyleSheets(adoptedStyleSheets)
    }

    /**
     * Adopt the provided style sheets into the shadow DOM
     *
     * @param styleSheets A collection of stylesheets that will be adopted by the shadow DOM
     */
    adoptStyleSheets(styleSheets: Array<CSSStyleSheet>): void {
      // If there is a shadow DOM
      if (this.shadowRoot) {
        styleSheets.forEach(styleSheet => {
          // Create a new stylesheet for the existing style sheet, as we are unable to import external stylesheets
          let constructedStyleSheet = new CSSStyleSheet({ media: styleSheet.media })

          try {
            Array.from(styleSheet.cssRules).forEach((rule, index) => {
              constructedStyleSheet.insertRule(rule.cssText, index)
            })
          }

          catch (e) {
            if (e instanceof DOMException) {
              console.warn(e)
            }

            else {
              throw e
            }
          }

          // Append it to the shadow DOM
          this.shadowRoot.adoptedStyleSheets.push(constructedStyleSheet)
        })
      }
    }

    /**
     * Returns the component property value. If the component has not yet been created, the value is
     * retrieved from the cached initialization values.
     */
    getInputValue(property: string): unknown {
      return this.runInZone(() => {
        let pendingValue = this.componentRef === null ?
          this.initialInputValues.get(property) :
          this.componentRef.instance[property]

        if (typeof pendingValue === 'function') {
          pendingValue = pendingValue.bind(this.componentRef?.instance)
        }

        return pendingValue
      })
    }

    /**
     * Sets the input value for the property. If the component has not yet been created, the value is
     * cached and set when the component is created.
     */
    setInputValue(property: string, value: unknown): void {
      this.runInZone(() => {
        if (this.componentRef === null) {
          this.initialInputValues.set(property, value)
          return
        }

        // Ignore the value if it is strictly equal to the current value, except if it is `undefined`
        // and this is the first change to the value (because an explicit `undefined` _is_ strictly
        // equal to not having a value set at all, but we still need to record this as a change).
        let inputValue = this.getInputValue(property)
        if (
          (value === inputValue || (Number.isNaN(value) && Number.isNaN(inputValue)))
          && !((value === undefined) && this.unchangedInputs.has(property))
        ) {
          return
        }

        // Record the changed value and update internal state to reflect the fact that this input has
        // changed.
        this.recordInputChange(property, value)
        this.unchangedInputs.delete(property)
        this.hasInputChanges = true

        // Update the component instance and schedule change detection.
        this.componentRef.instance[property] = value
        this.scheduleDetectChanges()
      })
    }

    /**
     * Set any stored initial inputs on the component's properties.
     */
    protected initializeInputs(): void {
      componentMirror?.inputs?.forEach(({ propName }) => {
        if (this.initialInputValues.has(propName)) {
          // Call `setInputValue()` now that the component has been instantiated to update its
          // properties and fire `ngOnChanges()`.
          this.setInputValue(propName, this.initialInputValues.get(propName))
        }
      })

      this.initialInputValues.clear()
    }

    /**
     * Sets up listeners for the component's outputs so that the events stream emits the events.
     */
    protected initializeOutputs(componentRef: ComponentRef<T>): void {
      const eventEmitters = componentMirror?.outputs?.map(({ propName, templateName }) => {
        const emitter: EventEmitter<unknown> = componentRef.instance[propName]
        return emitter.pipe(map(value => ({ name: templateName, value })))
      })

      this.eventEmitters.next(eventEmitters)
    }

    /**
     * Calls ngOnChanges with all the inputs that have changed since the last call.
     */
    protected callNgOnChanges(componentRef: ComponentRef<unknown>): void {
      if (!this.implementsOnChanges || this.inputChanges === null) {
        return
      }

      // Cache the changes and set inputChanges to null to capture any changes that might occur
      // during ngOnChanges.
      const inputChanges = this.inputChanges
      this.inputChanges = null;
      (componentRef.instance as OnChanges).ngOnChanges(inputChanges)
    }

    /**
     * Records input changes so that the component receives SimpleChanges in its onChanges function.
     */
    protected recordInputChange(property: string, currentValue: unknown): void {
      // Do not record the change if the component does not implement `OnChanges`.
      if (!this.implementsOnChanges) {
        return
      }

      if (this.inputChanges === null) {
        this.inputChanges = {}
      }

      // If there already is a change, modify the current value to match but leave the values for
      // `previousValue` and `isFirstChange`.
      const pendingChange = this.inputChanges[property]
      if (pendingChange) {
        pendingChange.currentValue = currentValue
        return
      }

      const isFirstChange = this.unchangedInputs.has(property)
      const previousValue = isFirstChange ? undefined : this.getInputValue(property)
      this.inputChanges[property] = new SimpleChange(previousValue, currentValue, isFirstChange)
    }

    /**
     * Listen for events  dispatch them as custom events.
     */
    private subscribeToEvents(): void {
      this.ngElementEventsSubscription = this.events.subscribe(e => {
        const customEvent = new CustomEvent(e.name, { detail: e.value })
        this.dispatchEvent(customEvent)
      })
    }

    /**
     * Marks the component view for check, if necessary.
     *
     * @remarks
     *
     * This is required when the `ChangeDetectionStrategy` is set to `OnPush`.
     */
    protected markViewForCheck(viewChangeDetectorRef: ChangeDetectorRef): void {
      if (this.hasInputChanges) {
        this.hasInputChanges = false
        viewChangeDetectorRef.markForCheck()
      }
    }

    /**
     * Schedules change detection to run on the component.
     * Ignores subsequent calls if already scheduled.
     */
    protected scheduleDetectChanges(): void {
      if (this.scheduledChangeDetectionFn) {
        return
      }

      this.scheduledChangeDetectionFn = Scheduler.scheduleBeforeRender(() => {
        this.scheduledChangeDetectionFn = null
        this.detectChanges()
      })
    }

    /**
     * Runs change detection on the component.
     */
    protected detectChanges(): void {
      if (this.componentRef === null) {
        return
      }

      this.callNgOnChanges(this.componentRef)

      if (this.viewChangeDetectorRef) {
        this.markViewForCheck(this.viewChangeDetectorRef)
      }

      this.componentRef.changeDetectorRef.detectChanges()
    }

    /**
     * Runs in the angular zone, if present.
     */
    private runInZone(fn: () => unknown) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (this.elementZone && (globalThis as any).Zone.current !== this.elementZone) ? this.ngZone.run(fn) : fn()
    }

  }

  // Return the extended element
  return ExtendedElement as unknown as CustomElementConstructor<T, E>
}
